import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import viewsApi from '../../services/views';

// First, create the read thunk
export const readViewsApi = createAsyncThunk(
	'views/readViewsApi',
	async (options, { rejectWithValue }) => {
		try {
			const response = await viewsApi.read(options);
			let views = [];
			if (response?.data && response?.data?.val) {
				let respViews = response.data.val;
				let defViews = respViews?.filter((view) => view?.isDefaultView);
				views = [...(options?.ALL_VIEW || []), ...response.data.val];
				if (defViews?.length) {
					views = [...views, ...defViews];
				}
			} else {
				views = [...(options?.ALL_VIEW || [])];
			}
			return views;
		} catch (err) {
			// Use `err.response.data` as `action.payload` for a `rejected` action,
			// by explicitly returning it using the `rejectWithValue()` utility
			return rejectWithValue(err.response ? err.response.data : err);
		}
	},
);

export const updateViewsApi = createAsyncThunk(
	'views/updateViewsApi',
	async (options, { rejectWithValue }) => {
		try {
			const response = await viewsApi.update(options);
			if (response?.data && response?.data?.val) {
				return response?.data?.val;
			}
			return options.data;
		} catch (err) {
			// Use `err.response.data` as `action.payload` for a `rejected` action,
			// by explicitly returning it using the `rejectWithValue()` utility
			return rejectWithValue(err.response ? err.response.data : err);
		}
	},
);

// This state 'data' could be different based on your needs
// ex: [{ viewid: 'ALL_VIEW', viewname: 'All', active: true }],
// ex: {doasho: {viewid: 'doasho', viewname: 'Doasho', active: false, filters: {}}}
const initialState = {
	data: [],
	status: 'idle',
	error: null,
	modalState: null,
	initialized: false,
	loadView: false,
	urlParams: '',
	currentRequestId: null,
	currentRequestScope: null,
};

const viewsSlice = createSlice({
	name: 'views',
	initialState,
	reducers: {
		setViews(state, action) {
			state.data = action.payload;
			state.initialized = true;
			return state;
		},
		resetViews(state, action) {
			return initialState;
		},
		setModalState(state, action) {
			state.modalState = action.payload;
		},
		setLoadView(state, action) {
			state.loadView = action.payload;
		},
		setViewsStatus(state, action) {
			state.status = action.payload;
		},
		setActiveView(state, action) {
			const { viewid } = action.payload;

			if (!viewid) {
				return;
			}

			const index = state.data.findIndex((view) => view.viewid === viewid);

			if (index !== -1) {
				state.data.forEach((view) => {
					view.active = false;
				});
				state.data[index] = { ...action.payload };
				state.data[index].active = true;
			}

			state.initialized = true;

			return state;
		},
		setUrlParams(state, action) {
			state.urlParams = action.payload;
		},
	},
	extraReducers: (builder) => {
		// Add reducers for additional action types here, and handle loading state as needed
		(builder.addCase(readViewsApi.fulfilled, (state, action) => {
			const requestScope = action.meta?.arg?.requestScope || null;
			// Ignore stale fulfillments from older requests.
			// This prevents cross-page/user races from overwriting current scope data.
			if (
				state.currentRequestId &&
				action.meta.requestId !== state.currentRequestId
			) {
				return;
			}
			// Additional guard if request IDs differ by middleware behavior but scope is clearly old.
			if (
				state.currentRequestScope &&
				requestScope &&
				requestScope !== state.currentRequestScope
			) {
				return;
			}

			// Ensure no views are marked as active initially - let the component determine this
			const processedViews =
				action?.payload?.map((view) => ({
					...view,
					active: false, // Let the component determine which should be active based on URL
				})) || [];

			state.data = processedViews;
			state.status = 'idle';
			state.initialized = true;
			state.currentRequestId = null;
		}),
			builder.addCase(readViewsApi.pending, (state, action) => {
				// Set state to loading
				state.status = 'loading';
				state.error = null;
				state.initialized = false;
				// Track latest request identity/scope so we can drop stale responses.
				state.currentRequestId = action.meta.requestId;
				state.currentRequestScope = action.meta?.arg?.requestScope || null;
			}),
			builder.addCase(readViewsApi.rejected, (state, action) => {
				// Ignore stale rejections from superseded requests.
				if (
					state.currentRequestId &&
					action.meta.requestId !== state.currentRequestId
				) {
					return;
				}
				// Set error state
				state.status = 'failed';
				state.error = action.error.message;
				state.initialized = true;
				state.currentRequestId = null;
				state.currentRequestScope = null;
			}),
			builder.addCase(updateViewsApi.fulfilled, (state, action) => {
				// API only returns user views, need to preserve default views
				const apiUserViews = action?.payload || [];
				const existingDefaultViews =
					state.data?.filter((v) => v.isDefaultView) || [];

				// Merge default views with updated user views
				state.data = [...existingDefaultViews, ...apiUserViews];
				state.status = 'idle';
			}),
			builder.addCase(updateViewsApi.pending, (state, action) => {
				// Set state to loading
				state.status = 'loading';
			}),
			builder.addCase(updateViewsApi.rejected, (state, action) => {
				// Set error state
				state.status = 'failed';
				state.error = action.error.message;
			}));
	},
});

// Export the selectors
export const selectViews = (state) => state.views.data;
export const selectLoadView = (state) => state.views.loadView;
export const selectViewsStatus = (state) => state.views.status;
export const selectModalState = (state) => state.views.modalState;
export const selectActiveView = (state) =>
	state.views.data ? state.views.data?.find((view) => view?.active) : null;
export const selectUrlParams = (state) => state.views.urlParams;
export const selectInitialized = (state) => state.views.initialized;

// Export the dispatch actions
export const {
	setViews,
	setModalState,
	setActiveView,
	setLoadView,
	setViewsStatus,
	resetViews,
	setUrlParams,
} = viewsSlice.actions;
// Export the reducer
export default viewsSlice.reducer;
